// export const BASE_URL = "http://localhost:8080";
export const BASE_URL = "https://api.eolass.com";

/**
 * Thrown by fetchRoute for any non-OK response. Carries the HTTP status and
 * parsed body along with the usual .message, so callers that need to branch
 * on *which* error happened (e.g. a 429 rate limit) don't have to guess
 * from message text.
 */
export class ApiError extends Error {
    status: number;
    body: unknown;

    constructor(message: string, status: number, body: unknown) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.body = body;
    }
}

/**
 * Navigates the whole page via a POST, by submitting a throwaway <form>.
 * Used for billing routes (checkout, manage subscription) where the
 * backend responds with a redirect to a hosted page — this needs a real
 * top-level navigation, not a fetch() call.
 */
export function submitPostRoute(path: string) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = BASE_URL.concat(path);
    document.body.appendChild(form);
    form.submit();
}

export async function analyzePrompt(prompt: string, file: File | null, length: "SHORT" | "MEDIUM" | "LONG") {
    const formData = new FormData();
    formData.append("prompt", prompt);
    formData.append("length", length);
    if (file) {
        formData.append("file", file);
    }

    try {
        const res = await fetch(BASE_URL.concat("/prompt"), {
            method: "POST",
            body: formData,
            credentials: "include",
        });
        return await res.json().catch(() => null);
    } catch (error) {
        throw new Error("Cannot connect to the server. Please try again.");
    }
}

/**
 * HTTP Request to the API
 * @param endpoint Appended directly after the base URL (include '/' at the start)
 * @param method HTTP method ('GET', 'POST', 'PUT', etc)
 * @param body HTTP request JSON body
 */
async function fetchRoute(endpoint: string, method: string, body?: unknown) {
    console.log(method + " " + endpoint);

    let res: Response;
    try {
        res = await fetch(BASE_URL.concat(endpoint), {
            method,
            headers: { "Content-Type": "application/json" },
            body: body !== undefined ? JSON.stringify(body) : undefined,
            credentials: "include",
        });
    } catch (err) {
        // Network-level failure (server down, no connection, CORS, etc.)
        throw new Error("Cannot connect to the server. Please try again.");
    }

    // Try to parse a body either way — error responses often carry a message.
    const data = await res.json().catch(() => null);

    if (!res.ok) {
        throw new ApiError(data?.message ?? `Request failed (${res.status})`, res.status, data);
    }

    return data;
}

/**
 * POST attempt to log in user
 * @param email The user's email
 * @param password The user's password
 */
export async function login(email: string, password: string) {
    return fetchRoute("/login", "POST", { email, password });
}

/**
 * POST attempt to register user
 * @param email
 * @param password
 * @param firstName
 * @param lastName
 * @param timezone
 */
export async function register(email: string, password: string, firstName: string, lastName: string, timezone: string) {
    return fetchRoute("/register", "POST", { email, password, firstName, lastName, timezone });
}

/**
 * Fetches information for the current logged-in user. Returns JSON in the form of:
 * {
 *     loggedIn: boolean
 *     verified: boolean // has the signup email code been confirmed yet
 *     initials: string
 *     subscribed: boolean
 *     timezone: string // IANA tz, e.g. "America/New_York"
 *     // Daily-limit gates for unsubscribed users — computed server-side
 *     // (today's usage vs. today's limit), not raw counts, so the frontend
 *     // never has to duplicate the comparison. Always true once subscribed.
 *     canCreateTree: boolean
 *     canOpenProblem: boolean
 * }
 */
export async function getMe() {
    return await fetchRoute("/me", "GET");
}

/**
 * Updates the user's stored timezone (used to compute their daily-limit
 * reset). Rate-limited server-side — a change made too soon after the last
 * one rejects with a 429, which fetchRoute surfaces as a thrown Error.
 * @param timezone An IANA timezone name, e.g. "America/New_York"
 */
export async function updateTimezone(timezone: string) {
    return await fetchRoute("/me/timezone", "PATCH", { timezone });
}

/**
 * Logs out the currently logged-in user
 */
export async function logout() {
    return await fetchRoute("/logout", "POST");
}

/**
 * Gets all the user's jobs
 */
export async function getJobs() {
    return await fetchRoute("/jobs", "GET");
}

/**
 * Gets job with id
 * @param id the id of the job
 */
export async function getJob(id: string) {
    return await fetchRoute(`/jobs/${id}`, "GET");
    // const res = await fetchRoute(`/jobs/${id}`, "GET");
    // console.log(res);
    // return res;
}

/**
 * Posts question response with id
 * @param id the id of the question
 * @param answer answer to the question
 */
export async function postQuestionResponse(id: string, answer: string) {
    return await fetchRoute(`/questions/${id}`, "POST", {answer: answer});
}

/**
 * Gets the text of node with id
 * @param id the id of the node
 */
export async function getNodeText(id: string): Promise<Response> {
    const res = await fetch(BASE_URL.concat(`/nodes/${id}`), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
    });

    if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message ?? `Request failed (${res.status})`);
    }

    return res; // don't consume the body here — let the caller stream it
}

export async function getProblemMeta(id: string) {
    const r = await fetchRoute(`/nodes/${id}/meta`, "GET");
    console.log(r)
    return r
    // return await fetchRoute(`/nodes/${id}/meta`, "GET");
}

export async function postProblemSolution(nodeID: string, solution: string) {
    return await fetchRoute(`/nodes/${nodeID}`, "POST", {
            answer: solution
        }
    );
}

export async function sendVerificationEmail() {
    return await fetchRoute("/send-email", "POST");
}

export async function verifyEmail(code: string) {
    return await fetchRoute("/verify-email", "POST", { code });
}